import React, { PropsWithChildren } from 'react';
import PermissionContext from './PermissionsContext';
import { ICCUser, isAllowedToUser, hasRequiredPermissions as _hasRequiredPermissions, FeatureFlagKeys } from '@communication-center/data-objects';
import { WMTooltip, WMTooltipProps } from '@walkme/wm-ui';
import { PermissionRestriction } from './Restricted';
import { useSelector } from '../store.interface';
import { withTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../components/AppPreloading/hooks/settings/useUserSettings';

const _WithTooltip = ({ t, element, ...rest }: { t: (key: string) => string; element: React.ReactNode; rest?: WMTooltipProps }) => {
    return (
        <WMTooltip title={t('common.dontHavePermissions')} {...rest}>
            <div style={{ cursor: 'default' }}>{element}</div>
        </WMTooltip>
    );
};
const WithTooltip = withTranslation('', { keyPrefix: '' })(_WithTooltip);

export const withPermissionTooltip = (element: React.JSX.Element, isAllowed: boolean, tooltipProps?: Partial<WMTooltipProps>): React.JSX.Element => {
    if (isAllowed) {
        return element;
    }
    return <WithTooltip element={element} {...(tooltipProps ?? {})} />;
};

export const useHasRequiredPermissions = (permissions: PermissionRestriction | PermissionRestriction[]) => {
    const permissionsFF = useFeatureFlag(FeatureFlagKeys.Permissions);
    const user = useSelector(state => state.common.user);
    if (!permissionsFF) {
        return true;
    }
    return _hasRequiredPermissions(user, permissions);
};

export const usePermissionCheck = ({
    creatorIds,
    permissions
}: {
    creatorIds?: number[];
    permissions?: PermissionRestriction | PermissionRestriction[];
}) => {
    const user = useSelector(state => state.common.user);
    const permissionsFF = useFeatureFlag(FeatureFlagKeys.Permissions);

    return !permissionsFF || isAllowedToUser({ creatorsIds: creatorIds, user, permissions });
};

const PermissionProvider: React.FC<React.PropsWithChildren<PropsWithChildren<{ user: ICCUser }>>> = ({ user, children }) => {
    const isAllowedTo = ({
        allowedRoles,
        creatorsIds,
        permissions
    }: {
        allowedRoles?: string[];
        creatorsIds?: number[];
        permissions?: PermissionRestriction | PermissionRestriction[];
    }) => {
        return !user || isAllowedToUser({ user, allowedRoles, creatorsIds, permissions });
    };

    return <PermissionContext.Provider value={{ isAllowedTo }}>{children}</PermissionContext.Provider>;
};

export default PermissionProvider;
